import React, { Component } from 'react'
import { connect } from 'react-redux'
import prepareComponent from 'client/shared/decorators/prepare-component'
import { withI18n, withI18nProps } from '@lingui/react'
import compose from 'lodash/fp/compose'

import errorBoundary, {
  ErrorBoundaryProps,
} from 'client/shared/decorators/error-boundary'
import urlFor, { QueryParams } from 'shared/tools/url-helper'
import { trimText } from 'client/shared/helpers/text-helpers'

import { loadPosts } from 'client/bookmate/reducers/shelf-reducer'
import { getPosts } from 'client/bookmate/reducers/posts-reducer'
import { getShelfById } from 'client/bookmate/reducers/shelves-reducer'
import { show } from 'client/shared/reducers/popup-reducer'

import Meta from 'client/shared/blocks/meta'
import HeadTitle from 'client/shared/blocks/head-title'
import InfiniteScroll from 'client/shared/blocks/infinite-scroll'
import Resource from 'client/shared/blocks/resource'
import SectionNavHeader from 'client/bookmate/blocks/section-nav-header'
import List from 'client/bookmate/blocks/list'

import { MAX_META_DESCRIPTION_LENGTH } from 'client/shared/constants/metadata'

import { Dispatch, State } from 'shared/types/redux'
import { Params } from 'client/shared/types/react-router'
import { ShelfProps } from 'client/shared/types/shelf'
import { BookPostProps } from 'client/shared/types/post'

type Props = {
  shelf: ShelfProps
  params: Params
  page: string
  isLoggedIn?: boolean
  dispatch: Dispatch
  posts: BookPostProps[]
  query?: QueryParams
} & ErrorBoundaryProps &
  withI18nProps

class ShelfBooksBox extends Component<Props> {
  load = (): void => {
    const {
      dispatch,
      params: { uuid },
      page,
    } = this.props

    return dispatch(loadPosts({ uuid, p: page, pp: 20, append: true }))
  }

  render(): JSX.Element | null {
    if (!this.props.shelf) return null

    const {
      shelf: { uuid, title, creator },
      posts,
      page,
      dispatch,
      isLoggedIn,
      query,
    } = this.props

    return (
      <div>
        <SectionNavHeader
          kind="shelf"
          query={query}
          intlId="shelf.title_posts"
          title={title}
          titleLink={urlFor.shelf(uuid, query)}
          creator={creator}
          isLoggedIn={isLoggedIn}
          showAuthPopup={() => dispatch(show())}
        />
        <InfiniteScroll load={this.load} page={Number(page)}>
          {this.renderMeta()}
          <List>
            {posts.map((post, i) => {
              const { resource, resourceType } = post

              if (['impression', 'quote'].includes(resourceType)) return null

              return <Resource key={i} item={resource} />
            })}
          </List>
        </InfiniteScroll>
      </div>
    )
  }

  renderMeta(): JSX.Element {
    const { shelf, i18n, posts } = this.props

    const { title, creator } = shelf

    const headTitle = i18n._('shelf.head_title_posts', {
      title,
      username: creator.name || creator.login,
    })
    let description = posts
      .slice(0, 20)
      .map(post => post.resource.title)
      .join(', ')
    description = trimText(description, MAX_META_DESCRIPTION_LENGTH)

    return (
      <div>
        <HeadTitle>{headTitle}</HeadTitle>
        <Meta name="description" content={description} />
        <Meta content={description} property="twitter:description" />
        <Meta content={description} property="og:description" />
      </div>
    )
  }
}

const prepareWrapper = prepareComponent(
  async function ({ store, params }) {
    const { uuid } = params
    const { dispatch } = store
    await dispatch(loadPosts({ uuid, p: 1, pp: 20 }))
  },
  {
    updateParams: 'uuid',
  },
)

const connectWrapper = connect((state: State) => ({
  app: state.app,
  posts: getPosts(state.shelf.posts, state.entities),
  page: state.shelf.pages.posts,
  shelf: getShelfById(state, state.shelf.uuid),
  isLoggedIn: state.currentUser.auth,
}))

const wrappers = compose(
  prepareWrapper,
  connectWrapper,
  errorBoundary,
  withI18n({ update: true }),
)

export default wrappers(ShelfBooksBox)
